import styles from './ccf-home.module.scss';
import { useTranslator } from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  getApplicationDirection,
  getAuthenticationStage,
  getRenderingVoicePrefPage,
  getSelectedMenuName,
  getShowPartnerAccessPage,
  globalActions,
} from '../../global.app.slice';
import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useHistory, useLocation } from 'react-router-dom';
import {
  CcfAcd,
  CcfAppHeader,
  CcfAppNavigation,
  CcfAppSpace,
  CcfDispositionInteraction,
  CcfInteractionWorkspace,
  CcfUnifiedInbox,
  CcfVoicePreference,
  LoginContainer,
  PartnerAccessControl,
  PersistentPanelFrame,
  activeContactSelector,
  getActivePersistentPanel,
  loginActions,
  selectInboxCollapsedState,
  selectUserLoggedInState,
} from '@nice-ccf/ui-components';
import {
  NavigationItem,
  useNavigationItems,
} from '../../ccf-navigation/useNavigationItems';
import { MediaType, PersistentPanel, StorageKeys } from '@nice-ccf/core-sdk';
import { Navigation } from '../../../enums/navigation-menus';
import ReactSplit, { SplitDirection } from '@devbookhq/splitter';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import CcfLiveDispositionInteraction from '../../ccf-disposition/ccf-live-disposition';
import { isEternalsEnv } from '../../../util/common';

let newTab: Window | null;

const createStyle = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  isAppToastMessageVisible: {
    pointerEvents: 'none',
  },
  navBarandSideBar: {
    display: 'flex',
    height: 'calc(100vh - 56px)',
    flex: 1,
    [theme.breakpoints.up('xl')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.down('xl')]: {
      flexDirection: 'column',
    },
  },
  interactionGrid: {
    [theme.breakpoints.up('xl')]: {
      width: '50%',
    },
  },
  alertStyle: {
    position: 'fixed',
    zIndex: '99',
    [theme.breakpoints.down('xl')]: {
      margin: '175px 5px 0px 5px',
    },
    [theme.breakpoints.up('xl')]: {
      margin: '100px 5px 0px 5px',
    },
  },
  parentClass: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  appSpaceContainer: {
    height: '100%',
    padding: '8px',
    position: 'relative',
  },
  detailsMenu: {
    [theme.breakpoints.down('xl')]: {
      overflowY: 'auto',
    },
    [theme.breakpoints.up('xl')]: {
      overflowY: 'hidden',
    },
  },
  customHorizontalSplitter: {
    padding: '0 1px',
    height: '100%',
    width: '10px',
    background: theme.palette.background.default,
    margin: '8px 0',
    '&:hover': {
      cursor: 'col-resize',
      background: theme.palette.text.noteLabel,
    },
    '&::after': {
      content: '" "',
      display: 'flex',
      background: theme.palette.text.secondary,
      borderRadius: '50px',
      position: 'absolute',
      width: '4px',
      height: '21px',
      top: '50%',
      opacity: '1',
    },
    '&:hover::after': {
      cursor: 'col-resize',
      background: theme.palette.background.paper,
    },
  },
  customDraggerSplitter: {
    display: 'none',
  },
  containerFullHeight: {
    height: '100%',
  },
  isNetworkOffline: {
    position: 'absolute',
    left: '50%',
    top: '50%',
  },
  mainViewContainer: {
    flex: 1,
    [theme.breakpoints.down('xl')]: {
      height: 'calc(100% - 53px)',
    },
    [theme.breakpoints.up('xl')]: {
      height: 'inherit',
    },
    overflowY: 'auto',
  },
}));

/* eslint-disable-next-line */
export interface CcfHomeProps {}

/**
 * Component displays home page static image for demo
 * @param props -CcfHomeProps
 * @returns home page static image for demo
 * ```
 * @example
 * const inboxCardState = 1
 * <CcfHome inboxCardState={inboxCardStateVal}/>
 * ```
 */
export function CcfHome(props: CcfHomeProps) {
  const { accounts, instance, inProgress } = useMsal();
  const isUserLoggedInSuccessfully = useSelector(selectUserLoggedInState);
  const isAuthenticated = useIsAuthenticated();
  const renderVoicePrefPage = useSelector(getRenderingVoicePrefPage);
  const [renderVoicePrefPageLS, setRenderVoicePrefPageLS] = useState(
    localStorage.getItem('voicePreferenceRememberOption') === 'true'
      ? true
      : false || false
  );
  const authenticationStage = useSelector(getAuthenticationStage);
  const showPartnerAccessBlock = useSelector(getShowPartnerAccessPage);
  const responsiveStyles = createStyle();
  const selectedMenu: string = useSelector(getSelectedMenuName);
  const [selectedMenuProperties, setSelectedMenuProperties] = useState<
    NavigationItem | undefined
  >();
  const [translate] = useTranslator();
  const theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const navigationItemList: NavigationItem[] = useNavigationItems(
    false,
    isSmView
  );
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const mediumView = useMediaQuery(theme.breakpoints.between('md', 'xl'));
  const activeContact = useSelector(activeContactSelector);
  const isInboxCollapsed = useSelector(selectInboxCollapsedState);
  const dispatch = useDispatch();
  window.addEventListener('load', () => {
    dispatch(globalActions.setPageReload(true));
  });
  const [sizes, setSizes] = useState([50, 50]);
  const history = useHistory();
  const [activeCustomSpaceIframe, setaAtiveCustomSpaceIframe] =
    useState<PersistentPanelFrame>();
  const { setUserDetails } = loginActions;
  const query = new URLSearchParams(useLocation().search);
  const queryparamVal = decodeURIComponent(query.get('userAccountID') || ' ');
  const [loading, setLoading] = useState(true);
  const activecontact = useSelector(activeContactSelector);
  const isCopilotSourceLiveLLM = activecontact?.copilotSource === 'Live LLM';
  
  useEffect(() => {
    // Check if there are query parameters
    if (window.location.search) {
      // Remove query parameters after page reload
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  /**
   *
   * @example
   */
  const decrypt = (text: string) => {
    if (text === ' ') return text;
    else {
      const cipher = text.split('');
      for (let i = 0; i < cipher.length; i++) {
        cipher[i] = String.fromCharCode(cipher[i].charCodeAt(0) - 2);
      }
      console.log(cipher.join(''));
      return cipher.join('');
    }
  };

  const paramVal = decrypt(queryparamVal);
  /**
   * Updating Tab index while Switching Tabs in home Panel to show tabs data
   * @param newTabIndex -number
   * @example - handleChange(1)
   */
  const handleResize = (gutterIdx: number, allSizes: number[]) => {
    const sizeWithFloorValues = allSizes.map((item) => Math.floor(item));
    if (sizeWithFloorValues[0] + sizeWithFloorValues[1] < 100) {
      const addVal = 100 - (sizeWithFloorValues[0] + sizeWithFloorValues[1]);
      sizeWithFloorValues[0] < sizeWithFloorValues[1]
        ? (sizeWithFloorValues[0] += addVal)
        : (sizeWithFloorValues[1] += addVal);
    }
    setSizes(sizeWithFloorValues);

    localStorage.setItem(
      StorageKeys.APPSPACE_RATIO,
      JSON.stringify(sizeWithFloorValues[1])
    );
    dispatch(
      globalActions.setAppSpaceRatio(JSON.stringify(sizeWithFloorValues[1]))
    );
    // dispatch(setLocalStorageKey({key:StorageKeys.APPSPACE_RATIO,value:JSON.stringify(sizeWithFloorValues)}));
  };


  // useEffect(() => { 
  //   console.log('useEffect');
  // },[renderVoicePrefPage]);

  useEffect(() => {
    if (!isAuthenticated && paramVal !== ' ' && inProgress === 'none') {
      // App 2 att'empts to acquire token silently using session or received data
      instance
        .ssoSilent({
          scopes: ['User.Read'],
          loginHint: paramVal,
          prompt: 'none',
        })
        .then((response) => {
          console.log('Authenticated in App 2 with token:', response);
          // Proceed with App 2 operations
          const loggedInUserName = accounts[0]?.name || ' ';
          const loginCredentialsArray = loggedInUserName?.split(' ');
          const loggedInUserDetails = {
            initials:
              loginCredentialsArray[0].charAt(0).toUpperCase() +
              loginCredentialsArray[1].charAt(0).toUpperCase(),
            firstName: loginCredentialsArray[0],
            lastName: loginCredentialsArray[1],
            email: accounts[0]?.username,
          };
          dispatch(setUserDetails(loggedInUserDetails));
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect({
              scopes: ['user.read'],
            });
          } else {
            console.error('Token acquisition failed in App 2:', error);
            // Optionally, handle other types of errors
          }

          setLoading(false); // Set loading to false even if authentication fails
        });
    }
  }, [inProgress]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false); // Set loading to false after successful authentication
    } else {
      if (inProgress === 'none' && paramVal === ' ') setLoading(false); // Set loading to false if authentication is not in progress
    }
  }, [isAuthenticated, inProgress]);

  useEffect(() => {
    const menuItem: NavigationItem | undefined = navigationItemList.find(
      (menu) => menu.menuName === selectedMenu
    );
    if (menuItem) {
      setSelectedMenuProperties(menuItem);
    } else {
      setSelectedMenuProperties({
        menuName: Navigation.INTERACTION,
        tooltip: translate('assignment'),
      });
    }
  }, [selectedMenu]);

  if (loading || inProgress !== 'none') {
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={110} />
      </Box>
    ); // Display loading div while ssoSilent is in progress
  }

  const renderMainContent = () => {
    return (
      <Box className={responsiveStyles.root}>
        <Box>
          <CcfAppHeader />
        </Box>
        <Box
          style={{ display: !showPartnerAccessBlock ? 'flex' : 'none' }}
          className={responsiveStyles.navBarandSideBar}
        >
          <Box>
            <CcfAppNavigation />
          </Box>
          <Box className={responsiveStyles.mainViewContainer}>
            <Grid container style={{ height: '100%' }}>
              <Grid
                item
                style={{
                  width: assignmentWidth,
                  height: '100%',
                }}
              >
                <CcfUnifiedInbox />
              </Grid>
              <Grid>
                {isCopilotSourceLiveLLM ? (
                  <CcfLiveDispositionInteraction />
                ) : (
                  (activeContact?.media === MediaType.VOICE ||
                    activeContact?.media === MediaType.VIDEOCALL ||
                    activeContact?.media === MediaType.VOICEMAIL) &&
                  selectedMenuProperties?.menuName ===
                    Navigation.INTERACTION && <CcfDispositionInteraction />
                )}
              </Grid>
              <Grid
                height={'100%'}
                id="agent_interactionSpace_parentContainer"
                style={{
                  width: canvasWidth,
                }}
                container
                display={'flex'}
                flex={'1 1 0%'}
                className={responsiveStyles.detailsMenu}
              >
                {selectedMenuProperties?.menuName ===
                  Navigation.INTERACTION &&
                ((activeContact?.media === MediaType.VOICE &&
                  'interaction' in activeContact) ||
                  activeContact?.media === MediaType.DIGITAL ||
                  activeContact?.media === MediaType.VIDEOCALL) &&
                !isSmView ? (
                  <ReactSplit
                    direction={SplitDirection.Horizontal}
                    initialSizes={sizes}
                    minWidths={[450, 350]}
                    onResizeFinished={handleResize}
                    gutterClassName={
                      responsiveStyles.customHorizontalSplitter
                    }
                    draggerClassName={responsiveStyles.customDraggerSplitter}
                  >
                    <Box className={responsiveStyles.containerFullHeight}>
                      <CcfInteractionWorkspace />
                    </Box>
                    <Box className={responsiveStyles.containerFullHeight}>
                      {!isSmView && (
                        <Grid
                          item
                          className={responsiveStyles.appSpaceContainer}
                        >
                          <CcfAppSpace />
                        </Grid>
                      )}
                    </Box>
                  </ReactSplit>
                ) : (
                  <>
                    {selectedMenuProperties?.menuName ===
                    Navigation.CUSTOMWORKSPACE ? (
                      selectedMenuProperties?.workSpaceComponent(
                        activeCustomSpaceIframe && activeCustomSpaceIframe
                      )
                    ) : selectedMenuProperties?.menuName !==
                      Navigation.INTERACTION ? (
                      selectedMenuProperties?.workSpaceComponent
                    ) : !isSmView ? (
                      <Grid
                        item
                        className={responsiveStyles.appSpaceContainer}
                        width={'100%'}
                      >
                        <CcfAppSpace />
                      </Grid>
                    ) : (
                      <CcfInteractionWorkspace />
                    )}
                    {selectedMenuProperties?.showAppSpace && !isSmView && (
                      <Grid
                        item
                        className={responsiveStyles.appSpaceContainer}
                        width={'100%'}
                      >
                        <CcfAppSpace />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
        <div style={{ display: showPartnerAccessBlock ? 'flex' : 'none' }}>
          <PartnerAccessControl />
        </div>
      </Box>
    );
  };

  let assignmentWidth = '';
  let canvasWidth = '';
  if (isInboxCollapsed) {
    assignmentWidth = '56px';
    canvasWidth = '100%';
  } else if (!isSmView) {
    assignmentWidth = '229px';
    canvasWidth = 'calc(100% - 285px)';
  } else if (mediumView) {
    assignmentWidth = '160px';
    canvasWidth = 'calc(100% - 216px)';
  } else if (isBelowMd) {
    assignmentWidth = '100%';
    canvasWidth = 'calc(100% - 266px)';
  } else {
    assignmentWidth = '56px';
    canvasWidth = '100%';
  }

  const renderContent = () => {
    if (isAuthenticated || isUserLoggedInSuccessfully) {
      if (!isEternalsEnv()) {
        switch (authenticationStage) {
          case 1:
            return <CcfAcd />;
          case 2:
            return <CcfVoicePreference />;
          case 3:
            return renderMainContent();
          default:
            return <div>Unexpected authentication stage. Please contact support.</div>; // Handle unexpected authenticationStage values
        }
      } else if (renderVoicePrefPage && !renderVoicePrefPageLS) {
        return <CcfVoicePreference />;
      } else {
        return renderMainContent();
      }
    } else {
      return <LoginContainer />;
    }
  };

  return renderContent();
}
